<template>
  <div class="d-flex align-content-center tests-container">
    <v-row>
      <v-col class="overflow-y-scroll">
        <v-expansion-panels
          v-model="panelOpen"
          tile
          flat
          v-if="illumination_tests || functional_tests"
        >
          <v-expansion-panel
            class="pt-0 mt-0"
            v-for="(test, i) in illumination_tests"
            :key="test.name"
          >
            <v-expansion-panel-header
              disable-icon-rotate
              class="pl-0 pr-2"
              :expand-icon="
                panelOpen === illumination_tests.indexOf(test)
                  ? '$iconChevronDown'
                  : '$iconChevronLeft'
              "
            >
              {{ test.name }}
            </v-expansion-panel-header>
            <v-expansion-panel-content class="pa-0 ma-0">
              <illumination-test-details :test="test" :index="i" />
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel
            v-for="(test, i) in functional_tests"
            :key="test.name"
          >
            <v-expansion-panel-header
              disable-icon-rotate
              class="pl-0 pr-2"
              :expand-icon="
                panelOpen ===
                functional_tests.indexOf(test) + illumination_tests.length
                  ? '$iconChevronDown'
                  : '$iconChevronLeft'
              "
            >
              {{ test.name }}
            </v-expansion-panel-header>
            <v-expansion-panel-content class="pa-0">
              <functional-test-details :test="test" :index="i" />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-row>
          <v-col
            :class="
              (illumination_tests && illumination_tests.length > 0) ||
              (functional_tests && functional_tests.length > 0)
                ? 'text-right'
                : 'text-center'
            "
          >
            <NikoTextButton
              color="primary"
              :text="$t('room.tests.button')"
              icon-before="$iconAdd"
              @onClick="showModal = true"
            />
          </v-col>
        </v-row>

        <niko-modal-wrapper
          :primaryButtonText="$t('room.tests.modal.add')"
          :secondaryButtonText="$t('room.tests.modal.cancel')"
          :title="$t('room.tests.modal.title')"
          :showModal="showModal"
          @onClose="showModal = false"
          @onSubmit="addATest()"
        >
          <v-radio-group v-model="testType">
            <v-radio
              v-for="testType in testTypes"
              :key="testType.value"
              :label="testType.label"
              :value="testType.value"
            ></v-radio>
          </v-radio-group>
        </niko-modal-wrapper>
      </v-col>
    </v-row>
    <guidance-button page="configuration.room_tests" bottom="106" />
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import NikoTextButton from "@/components/UI/NikoTextButton";
import NikoModalWrapper from "@/components/UI/NikoModalWrapper";
import GuidanceButton from "@/components/guidance/GuidanceButton";
import IlluminationTestDetails from "@/components/sidebar/IlluminationTestDetails";
import FunctionalTestDetails from "@/components/sidebar/FunctionalTestDetails";

export default {
  name: "room-tests-tab",
  components: {
    IlluminationTestDetails,
    FunctionalTestDetails,
    NikoModalWrapper,
    NikoTextButton,
    GuidanceButton,
  },
  data: function () {
    return {
      panelOpen: 0,
      showModal: false,
      testType: "Illumination test",
      testTypes: [
        {
          label: this.$i18n.t("room.tests.modal.illumination"),
          value: "Illumination test",
        },
        {
          label: this.$i18n.t("room.tests.modal.functional"),
          value: "Functional test",
        },
      ],
    };
  },
  computed: {
    ...mapState("roomStore", [
      "illumination_tests",
      "functional_tests",
      "room_type_id",
      "room_types",
    ]),
  },
  methods: {
    ...mapMutations("roomStore", ["addIlluminationTest", "addFunctionalTest"]),
    addATest() {
      let test = {
        name:
          this.testType === "Illumination test"
            ? `${this.$i18n.t("room.illumination.name")} ${
                this.illumination_tests.length + 1
              }`
            : `${this.$i18n.t("room.functional.name")} ${
                this.functional_tests.length + 1
              }`,
        test_requirements: "",
      };

      if (this.testType === "Illumination test") {
        const average_illuminance =
          this.room_type_id !== -1 && this.room_type_id !== null
            ? this.room_types.find(
                (type) => type.room_type_id === this.room_type_id
              )?.lux
            : 0;
        const uniformity =
          this.room_type_id !== -1 && this.room_type_id !== null
            ? this.room_types.find(
                (type) => type.room_type_id === this.room_type_id
              )?.u0
            : 0;
        const required_ra =
          this.room_type_id !== -1 && this.room_type_id !== null
            ? this.room_types.find(
                (type) => type.room_type_id === this.room_type_id
              )?.ra
            : 0;
        const required_rugl =
          this.room_type_id !== -1 && this.room_type_id !== null
            ? this.room_types.find(
                (type) => type.room_type_id === this.room_type_id
              )?.rugl
            : 0;
        test = {
          ...test,
          average_illuminance,
          illuminance_deviation: 10,
          uniformity,
          required_ra,
          required_rugl,
          measured_ra: required_ra,
          measured_rugl: required_rugl,
          include_flicker: true,
          include_ra_rugl_maintenance: true,
          maintenance_factor: 1,
          furniture: "not_relevant",
        };
      } else if (this.testType === "Functional test") {
        test = {
          ...test,
          daylight_control_enabled: true,
          daylight_zones_enabled: true,
          required_number_of_zones: 0,
          required_number_of_zones_unknown: false,
          motion_detectors_enabled: true,
          light_on_automatic: true,
          light_off_delay: 5,
        };
      }

      this.testType === "Illumination test"
        ? this.addIlluminationTest(test)
        : this.addFunctionalTest(test);
      this.showModal = false;
      this.panelOpen =
        this.illumination_tests.length + this.functional_tests.length - 1;
    },
  },
};
</script>

<style lang="scss">
.tests-container {
  .v-item-group {
    z-index: auto;
  }

  .v-expansion-panel-content__wrap {
    padding: 0 !important;
  }
}
</style>
